import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import MenuMappingTree from './MenuMappingTree';
import { menuService } from '../../../services/menuService';
import { useAuth } from '../../../contexts/AuthContext';

const MenuMappingPanel = ({ catalogId, totalPages }) => {
  const { t } = useTranslation();
  const { userProfile, activeCompanyId } = useAuth();
  const [tree, setTree] = useState([]);
  const [mappings, setMappings] = useState({}); // { nodeId: pageNumber }
  const [loading, setLoading] = useState(true);
  const [savingNodeId, setSavingNodeId] = useState(null);
  const [error, setError] = useState(null);

  const companyId = activeCompanyId || userProfile?.company_id;

  useEffect(() => {
    if (!companyId || !catalogId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [treeData, rawMappings] = await Promise.all([
          menuService.getMenuTree(companyId),
          menuService.getMappingsByCatalog(catalogId),
        ]);
        setTree(treeData);
        const map = {};
        rawMappings.forEach((m) => { map[m.nodeId] = m.pageNumber; });
        setMappings(map);
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId, catalogId]);

  const handleMappingChange = async (nodeId, pageNumber) => {
    const previous = mappings[nodeId];
    // Optimistic update
    setMappings((prev) => {
      const next = { ...prev };
      if (pageNumber === null) delete next[nodeId];
      else next[nodeId] = pageNumber;
      return next;
    });
    setSavingNodeId(nodeId);
    try {
      if (pageNumber === null) {
        await menuService.deleteCatalogMapping(nodeId, catalogId);
      } else {
        await menuService.saveCatalogMapping(nodeId, catalogId, pageNumber);
      }
    } catch (err) {
      // Revert on error
      setMappings((prev) => {
        const next = { ...prev };
        if (previous === undefined) delete next[nodeId];
        else next[nodeId] = previous;
        return next;
      });
      setError(err?.message);
    } finally {
      setSavingNodeId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon name="Loader2" size={20} className="text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (tree.length === 0) {
    return (
      <div className="p-4 text-center">
        <Icon name="Menu" size={32} className="text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">{t('menuMapping.noMenu')}</p>
        <a href="/menu-settings" className="text-xs text-accent underline mt-1 inline-block">
          {t('menuMapping.goToSettings')}
        </a>
      </div>
    );
  }

  return (
    <div>
      <div className="px-3 py-2 border-b border-border">
        <p className="text-xs text-muted-foreground">{t('menuMapping.subtitle')}</p>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </div>
      )}

      {savingNodeId && (
        <div className="px-3 pt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <Icon name="Loader2" size={12} className="animate-spin" />
          {t('menuMapping.saving')}
        </div>
      )}

      <MenuMappingTree
        tree={tree}
        totalPages={totalPages}
        mappings={mappings}
        onMappingChange={handleMappingChange}
      />
    </div>
  );
};

export default MenuMappingPanel;
